/**
 * What this window/tab can see about its own use, for core Updates.safeToRestart: the last keyboard/pointer input,
 * whether it is hidden, and what a restart would break (a call, composer text, sends, uploads). Features register a
 * source for the parts only they know (WorkspaceRoot: the huddle, the upload queue, writes in flight).
 */
import type { AppActivity } from "@bundle/core";

type Source = () => Partial<Pick<AppActivity, "inCall" | "composerText" | "pendingWrites" | "uploads">>;

const sources = new Map<string, Source>();
let lastInputAt = Date.now();
let listening = false;

/** Composers whose unsent text a restart must not lose (their `testID`s; drafts also save, this is the belt). */
const COMPOSERS = ["composer", "thread-composer", "edit-composer", "mail-composer"];

function listen(): void {
  if (listening || typeof window === "undefined") return;
  listening = true;
  const touch = () => {
    lastInputAt = Date.now();
  };
  for (const type of ["keydown", "pointerdown", "wheel", "touchstart"] as const) window.addEventListener(type, touch, { capture: true, passive: true });
}

/** Text typed into any open composer (message, thread, edit or mail). */
export function composerHasText(doc: Document = document): boolean {
  for (const id of COMPOSERS) {
    for (const root of Array.from(doc.querySelectorAll(`[data-testid="${id}"]`))) {
      for (const field of Array.from(root.querySelectorAll<HTMLElement>('textarea, input[type="text"], [contenteditable="true"]'))) {
        const text = field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement ? field.value : (field.textContent ?? "");
        if (text.trim() !== "") return true;
      }
    }
  }
  return false;
}

/** Adds what a feature knows (e.g. "huddle" → inCall). Returns the remover. */
export function addActivitySource(id: string, source: Source): () => void {
  sources.set(id, source);
  return () => {
    if (sources.get(id) === source) sources.delete(id);
  };
}

/** This window right now. */
export function currentActivity(): AppActivity {
  listen();
  const a: AppActivity = {
    lastInputAt,
    hidden: typeof document !== "undefined" && document.visibilityState === "hidden",
    inCall: false,
    composerText: typeof document !== "undefined" && composerHasText(),
    pendingWrites: 0,
    uploads: 0,
  };
  for (const source of sources.values()) {
    try {
      const s = source();
      if (s.inCall === true) a.inCall = true;
      if (s.composerText === true) a.composerText = true;
      a.pendingWrites += s.pendingWrites ?? 0;
      a.uploads += s.uploads ?? 0;
    } catch {
      // A source that throws can't vouch for its feature: count it as busy.
      a.pendingWrites += 1;
    }
  }
  return a;
}

/** Starts tracking input (call once at startup; currentActivity also starts it). */
export function startActivityTracking(): void {
  listen();
}
